import React, { useRef, useState } from 'react';
import { useT } from '../i18n';
import './UploadModal.css';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];

export const UploadModal = ({ isOpen, onClose, onSingleUpload, onBulkUpload }) => {
  const { t } = useT();
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');
  const singleInputRef = useRef(null);
  const bulkInputRef = useRef(null);

  if (!isOpen) return null;

  const validateFiles = (files) => {
    const validFiles = [];
    const errors = [];

    for (const file of files) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        errors.push(t('upload.badFormat', { name: file.name }));
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(t('upload.tooBig', { name: file.name }));
        continue;
      }
      validFiles.push(file);
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    return validFiles;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    setError('');

    const files = Array.from(e.dataTransfer.files);
    const validFiles = validateFiles(files);

    if (validFiles.length === 0) return;

    if (validFiles.length === 1) {
      onSingleUpload(validFiles[0]);
    } else {
      onBulkUpload(validFiles);
    }
    onClose();
  };

  const handleSingleFileSelect = (e) => {
    setError('');
    const file = e.target.files?.[0];
    if (!file) return;

    const validFiles = validateFiles([file]);
    if (validFiles.length === 1) {
      onSingleUpload(validFiles[0]);
      onClose();
    }
    // Reset input
    e.target.value = '';
  };

  const handleBulkFileSelect = (e) => {
    setError('');
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = validateFiles(files);
    if (validFiles.length > 0) {
      onBulkUpload(validFiles);
      onClose();
    }
    // Reset input
    e.target.value = '';
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="upload-modal-overlay" onClick={handleBackdropClick}>
      <div className="upload-modal-container">
        {/* Header */}
        <div className="upload-modal-header">
          <div className="upload-modal-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <div className="upload-modal-title-section">
            <h2>Upload files</h2>
            <p>Select and upload the files of your choice</p>
          </div>
        </div>

        {/* Drop Zone */}
        <div
          className={`upload-modal-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="upload-modal-folder-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="#9ca3af">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <p className="upload-modal-dropzone-text">{t('upload.dropText')}</p>
          <p className="upload-modal-dropzone-hint">{t('upload.dropHint')}</p>

          {/* Buttons */}
          <div className="upload-modal-buttons">
            <button
              className="upload-modal-btn upload-modal-btn-single"
              onClick={() => singleInputRef.current?.click()}
            >
              {t('upload.single')}
            </button>
            <button
              className="upload-modal-btn upload-modal-btn-bulk"
              onClick={() => bulkInputRef.current?.click()}
            >
              {t('upload.bulk')}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="upload-modal-error">
            {error}
          </div>
        )}

        {/* Hidden File Inputs */}
        <input
          ref={singleInputRef}
          type="file"
          accept="image/png,image/jpeg,application/pdf"
          style={{ display: 'none' }}
          onChange={handleSingleFileSelect}
        />
        <input
          ref={bulkInputRef}
          type="file"
          accept="image/png,image/jpeg,application/pdf"
          multiple
          style={{ display: 'none' }}
          onChange={handleBulkFileSelect}
        />

        {/* Close Button */}
        <button className="upload-modal-close" onClick={onClose}>
          ×
        </button>
      </div>
    </div>
  );
};

export default UploadModal;
